import api from "../services/authService";

/**
 * Client-side plausibility gate for fax numbers, shared by every send
 * surface (context-menu modal, Fax page form) so the rule can't drift
 * between them. The backend re-validates strictly and is the
 * authority — this only exists to disable Send buttons early.
 */
export const isPlausibleFaxNumber = (raw) => {
  const trimmed = (raw || "").trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+")) {
    return digits.length >= 8 && digits.length <= 15;
  }
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
};

/**
 * Queue an outbound fax of a document (backend: dms_backend
 * POST /api/documents/<pk>/fax/). Returns the OutboundFax receipt
 * ({ id, status, to_number, ... }); delivery outcome arrives later as
 * a FAX_SENT / FAX_FAILED notification to the requesting user.
 */
export const sendDocumentFax = async (
  documentId,
  toNumber,
  { coverTo = "", coverSubject = "", fromNumber = "", matterId = "" } = {},
) => {
  // Omit empty optionals (rather than sending "") so both send
  // surfaces submit identical payload shapes for the no-cover /
  // firm-default case — sendFax below does the same.
  const payload = { to_number: toNumber };
  if (coverTo) payload.cover_to = coverTo;
  if (coverSubject) payload.cover_subject = coverSubject;
  if (fromNumber) payload.from_number = fromNumber;
  if (matterId) payload.matter_id = matterId;
  const response = await api.post(`/documents/${documentId}/fax/`, payload);
  return response;
};

/**
 * The firm's active fax lines ({ number, label }) — feeds the
 * "Send from" dropdown on both send surfaces.
 */
export const fetchFaxLines = async () => {
  const response = await api.get(`/fax/lines/`);
  return response;
};

/** Lightweight type-ahead results for the optional outbound-fax matter. */
export const searchFaxMatters = async (query) => {
  const response = await api.get(
    `/mail-intake/matter-search/?q=${encodeURIComponent(query)}`,
    { timeout: 15000 },
  );
  return response;
};

/**
 * Render exactly what a send would transmit (merged cover+document
 * PDF, or the bare document when no cover fields are set) WITHOUT
 * queueing anything. Returns a PDF Blob response; callers typically
 * open it via URL.createObjectURL.
 */
export const previewFax = async ({
  toNumber = "",
  file = null,
  documentId = "",
  coverTo = "",
  coverSubject = "",
  fromNumber = "",
}) => {
  const form = new FormData();
  if (toNumber) form.append("to_number", toNumber);
  if (file) form.append("file", file);
  if (documentId) form.append("document_id", documentId);
  if (coverTo) form.append("cover_to", coverTo);
  if (coverSubject) form.append("cover_subject", coverSubject);
  if (fromNumber) form.append("from_number", fromNumber);
  const response = await api.post(`/fax/preview/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    responseType: "blob",
  });
  return response;
};

/**
 * The transmitted PDF for one outbound fax (dispatch-time archive of
 * the exact bytes Phaxio received; older rows regenerate
 * cover+document server-side). Returns a PDF Blob response.
 */
export const fetchOutboundArtifact = async (outboundFaxId) => {
  const response = await api.get(`/fax/outbound/${outboundFaxId}/artifact/`, {
    responseType: "blob",
  });
  return response;
};

/**
 * Open a synchronous placeholder tab DURING the click gesture, before
 * any await — popup blockers only allow window.open tied to a user
 * gesture, so opening after an async fetch gets silently blocked
 * (Bugbot #253). Pass the returned handle to openPdfBlob once the
 * bytes arrive; returns null when the blocker won anyway.
 */
export const openPlaceholderTab = () => {
  try {
    return window.open("", "_blank");
  } catch {
    return null;
  }
};

/**
 * Show a PDF Blob response in the given placeholder tab (from
 * openPlaceholderTab). Falls back to downloading the file when the
 * tab was blocked, so Preview never silently does nothing. The object
 * URL is revoked after the tab has had time to load it.
 */
export const openPdfBlob = (blob, tab = null) => {
  const url = URL.createObjectURL(
    new Blob([blob], { type: "application/pdf" })
  );
  if (tab && !tab.closed) {
    tab.location = url;
  } else {
    // No "noopener" here: it makes window.open return null even on
    // success, which made the fallback download AND open (Bugbot
    // #253 round 2). Blob URLs are same-origin, so noopener adds
    // nothing.
    const opened = window.open(url, "_blank");
    if (!opened) downloadPdfBlob(blob, "fax-preview.pdf");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Close a placeholder tab after a failed fetch (no orphan blank tabs). */
export const closePlaceholderTab = (tab) => {
  try {
    tab?.close();
  } catch {
    /* already gone */
  }
};

/** Save a PDF Blob response to disk under the given filename. */
export const downloadPdfBlob = (blob, filename) => {
  const url = URL.createObjectURL(
    new Blob([blob], { type: "application/pdf" })
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || "fax.pdf";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Poll one outbound fax's status. */
export const fetchOutboundFaxStatus = async (outboundFaxId) => {
  const response = await api.get(`/fax/outbound/${outboundFaxId}/`);
  return response;
};

/**
 * Paged log of RECEIVED faxes (all dispositions — still in the mail
 * sorting queue, assigned to a matter, or deleted). Rows carry a
 * short-lived `preview_url` for inline PDF preview.
 */
export const fetchFaxLog = async ({
  page = 1,
  pageSize = 20,
  search = "",
  line = "",
} = {}) => {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (search) params.append("search", search);
  // Optional: only faxes dialed to this registered fax line (E.164).
  if (line) params.append("line", line);
  const response = await api.get(`/fax/log/?${params.toString()}`);
  return response;
};

/**
 * Paged outbound history (own sends; admins see firm-wide). With
 * `line` (E.164), instead every send from that shared fax line —
 * rows carry `can_open_artifact` (own sends + admins only).
 */
export const fetchOutboundFaxes = async ({ page = 1, pageSize = 20, line = "" } = {}) => {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (line) params.append("line", line);
  const response = await api.get(`/fax/outbound/list/?${params.toString()}`);
  return response;
};

/**
 * Send a fax from the Fax page: `to_number` plus exactly one of
 * `file` (a PDF File object) or `documentId` (an existing DMS
 * document). `coverTo` / `coverSubject`, when provided, make the
 * backend prepend a branded cover page. Returns the OutboundFax
 * receipt.
 */
export const sendFax = async ({
  toNumber,
  file = null,
  documentId = "",
  coverTo = "",
  coverSubject = "",
  fromNumber = "",
  matterId = "",
}) => {
  const form = new FormData();
  form.append("to_number", toNumber);
  if (file) form.append("file", file);
  if (documentId) form.append("document_id", documentId);
  if (coverTo) form.append("cover_to", coverTo);
  if (coverSubject) form.append("cover_subject", coverSubject);
  if (fromNumber) form.append("from_number", fromNumber);
  if (matterId) form.append("matter_id", matterId);
  const response = await api.post(`/fax/send/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response;
};
